import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguageContext } from "../../i18n/LanguageContext";
import { useTheme } from "../../context/ThemeContext";

interface LanguageToggleProps {
  className?: string;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({ className = "" }) => {
  const { i18n } = useTranslation();
  const { setLanguage } = useLanguageContext();
  const { theme } = useTheme();

  const currentLanguage = i18n.language;
  const isDark = theme === "dark";
  const isEnglish = currentLanguage === "en";

  const handleLanguageToggle = () => {
    const newLanguage = isEnglish ? "tr" : "en";
    setLanguage(newLanguage);
  };

  return (
    <div className={`language-toggle-container ${className}`}>
      <button
        onClick={handleLanguageToggle}
        className="language-toggle-switch"
        title={isEnglish ? "Switch to Turkish" : "Switch to English"}
        aria-label={isEnglish ? "Switch to Turkish" : "Switch to English"}
        style={{
          position: "relative",
          width: "70px",
          height: "36px",
          border: "none",
          borderRadius: "18px",
          background: isDark
            ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
            : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
          cursor: "pointer",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isDark
            ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)"
            : "inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)",
          outline: "none",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.05)";
          e.currentTarget.style.boxShadow = isDark
            ? "inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)"
            : "inset 0 2px 4px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = isDark
            ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)"
            : "inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)";
        }}
      >
        {/* Track Flags */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "8px",
            transform: "translateY(-50%)",
            fontSize: "16px",
            transition: "all 0.3s ease",
            opacity: isEnglish ? 0.4 : 1,
            filter: isEnglish ? "grayscale(100%)" : "none",
          }}
        >
          🇹🇷
        </div>

        <div
          style={{
            position: "absolute",
            top: "50%",
            right: "8px",
            transform: "translateY(-50%)",
            fontSize: "16px",
            transition: "all 0.3s ease",
            opacity: isEnglish ? 1 : 0.4,
            filter: isEnglish ? "none" : "grayscale(100%)",
          }}
        >
          🇬🇧
        </div>

        {/* Switch Handle */}
        <div
          style={{
            position: "absolute",
            top: "3px",
            left: isEnglish ? "37px" : "3px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: isDark
              ? "linear-gradient(135deg, #4a5568 0%, #2d3748 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
            boxShadow: isDark
              ? "0 2px 6px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
              : "0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          {/* Active Flag in Handle */}
          <span
            style={{
              fontSize: "18px",
              filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
              transition: "all 0.3s ease",
            }}
          >
            {isEnglish ? "🇬🇧" : "🇹🇷"}
          </span>
        </div>

        {/* Glow Effect */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: isEnglish ? "52px" : "18px",
            transform: "translate(-50%, -50%)",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            background: isEnglish
              ? "radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, transparent 70%)",
            transition: "all 0.4s ease",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        {/* Language Labels */}
        <div
          style={{
            position: "absolute",
            bottom: "-20px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "10px",
            fontWeight: "600",
            color: isDark ? "#a0aec0" : "#4a5568",
            whiteSpace: "nowrap",
            opacity: 0.7,
          }}
        >
          {isEnglish ? "EN" : "TR"}
        </div>
      </button>
    </div>
  );
};

export default LanguageToggle;
